import axios, { HttpStatusCode } from 'axios';
import type { ApiResponse } from 'shared/api';
import {
  completeIterationImagesUploadRequest,
  ITERATION_DATASET_URL,
  queryClient,
} from 'shared/api';
import autoRefreshApi from 'shared/api/auto-refresh-api';
import { apiDataResponseMapper, apiPayloadMapper } from 'shared/api/utils';
import { EnvVariables } from 'shared/env-variables';
import { REQUEST_TIMEOUT_MS } from './constants';
import { ImagesUploadCompletionStatus } from './enums';
import type {
  ImageUploadSuccessPayload,
  PresignedUrls,
  PresignedUrlsPayload,
  PutImageOptions,
  UploadedImagesList,
} from './types';
import { getSignedUrlUploadHeaders } from './utils/signed-url-upload-utils';

const getIterationDatasetPath = (iterationDatasetId: string) =>
  `processing/iteration-dataset/${iterationDatasetId}`;

export const fetchPresignedUrls = async (
  iterationDatasetId: string,
  filenames: string[],
  signal: AbortSignal,
): Promise<string[]> => {
  const response = await autoRefreshApi.post<unknown, ApiResponse<object>>(
    `${getIterationDatasetPath(iterationDatasetId)}/images-presigned-url/`,
    apiPayloadMapper<PresignedUrlsPayload, object>({ filenames }),
    { signal, timeout: REQUEST_TIMEOUT_MS },
  );

  return apiDataResponseMapper<object, PresignedUrls>(response.data)
    .presignedUrls;
};

export const putImage = async (
  signedUrl: string,
  file: File,
  { signal, onUploadProgress }: PutImageOptions,
): Promise<void> => {
  await axios.put(signedUrl, new Blob([file], { type: file.type }), {
    headers: getSignedUrlUploadHeaders(signedUrl),
    signal,
    onUploadProgress: ({ loaded }) => onUploadProgress(loaded),
  });
};

export const markImageUploaded = async (
  iterationDatasetId: string,
  imageFilename: string,
  signal: AbortSignal,
): Promise<void> => {
  await autoRefreshApi.post(
    `${getIterationDatasetPath(iterationDatasetId)}/image-upload-success/`,
    apiPayloadMapper<ImageUploadSuccessPayload, object>({ imageFilename }),
    { signal, timeout: REQUEST_TIMEOUT_MS },
  );
};

export const fetchUploadedImageNames = async (
  iterationDatasetId: string,
  signal?: AbortSignal,
): Promise<string[]> => {
  const response = await autoRefreshApi.get<unknown, ApiResponse<object>>(
    `${getIterationDatasetPath(iterationDatasetId)}/images-list/`,
    { signal, timeout: REQUEST_TIMEOUT_MS },
  );

  return (
    apiDataResponseMapper<object, UploadedImagesList>(response.data)
      .filenames ?? []
  );
};

export const pingBackend = async (): Promise<boolean> => {
  try {
    const response = await autoRefreshApi.get<unknown, ApiResponse<string>>(
      `${EnvVariables.backendUrl.toString()}/ping`,
      { timeout: REQUEST_TIMEOUT_MS },
    );
    return response.meta.status_code === HttpStatusCode.Ok;
  } catch {
    return false;
  }
};

export const completeImagesUpload = async (
  iterationDatasetId: string,
): Promise<void> => {
  await completeIterationImagesUploadRequest({
    iterationDatasetId,
    uploadStatus: ImagesUploadCompletionStatus.Done,
  });
};

// A partial key matches every useIterationDatasetRequest query for that URL, regardless of the payload it was called with.
export const invalidateIterationDataset = () =>
  queryClient.invalidateQueries({ queryKey: [ITERATION_DATASET_URL] });
